import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { PAGINATOR_PAGE_SIZES } from "../../common/constants";
import { getPageOffset, paginate } from "../../common/pagination";
import Paginator from "../../Paginator/Paginator";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function Pagination(props) {
  const {
    defaultPage,
    defaultItemsPerPage,
    pageSizes,
    container,
    noItemsPerPageMenu,
    disabled,
    paginatorCustomStrings,
    "aria-label": ariaLabel,
    children,
    data,
    navigationTooltipPosition,
    className,
    noHorizontalPadding,
    // eslint-disable-line react/prop-types
    remote,
    totalItems,
    onPagination
  } = props;
  const [pageItem, setPageItem] = useState(defaultPage);
  const [itemsPerPageCount, setItemsPerPageCount] = useState(defaultItemsPerPage);
  const paginatorContainerNode = useRef(document.createElement("div"));
  useEffect(() => {
    let paginatorRootNode = "";
    const containerNode = paginatorContainerNode.current;
    /* istanbul ignore else */

    if (container) {
      paginatorRootNode = container.current;
      paginatorRootNode.appendChild(containerNode);
    }
    /*
     * Do not remove the container node if it was passed as a ref;
     * the node's rendering is handled by another React Element.
     */


    return () => {
      /* istanbul ignore else */
      if (container && paginatorRootNode) {
        paginatorRootNode.removeChild(containerNode);
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container]);
  /**
   * When client side pagination is enabled and some items(from `data` prop) are removed,
   * We need to update the page according to the new items.
   */

  useEffect(() => {
    const {
      remote,
      data
    } = props;
    const totalItems = data.length;

    if (!remote && totalItems > 0) {
      const currentPage = pageItem;
      const itemsPerPage = itemsPerPageCount;
      const lastPage = Math.ceil(totalItems / itemsPerPage);

      if (currentPage > lastPage) {
        setPageItem(lastPage);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [data, pageItem, itemsPerPageCount]);

  function getPage() {
    return props.page !== null ? props.page : pageItem;
  }

  function getItemsPerPage() {
    return props.itemsPerPage !== null ? props.itemsPerPage : itemsPerPageCount;
  }

  function getTotalItems() {
    return remote ? totalItems : data.length;
  }

  const handlePagination = (itemPage, itemsPerPage) => {
    if (props.page !== null) {
      onPagination && onPagination(itemPage, itemsPerPage);
    } else {
      setPageItem(itemPage);
      setItemsPerPageCount(itemsPerPage);
      onPagination && onPagination(itemPage, itemsPerPage);
    }
  };

  const page = getPage();
  const itemsPerPage = getItemsPerPage();
  const paginatedComponent = children({
    paginatedData: remote ? data : paginate(data, page, itemsPerPage),
    pageOffset: getPageOffset(page, itemsPerPage),
    remote
  });
  return _jsxs(React.Fragment, {
    children: [/*#__PURE__*/ReactDOM.createPortal(_jsx(Paginator, {
      className: className,
      page: page,
      itemsPerPage: itemsPerPage,
      pageSizes: pageSizes,
      noItemsPerPageMenu: noItemsPerPageMenu,
      disabled: disabled,
      totalItems: getTotalItems(),
      "aria-label": ariaLabel,
      onPagination: handlePagination,
      customStrings: paginatorCustomStrings,
      navigationTooltipPosition: navigationTooltipPosition,
      noHorizontalPadding: noHorizontalPadding
    }), paginatorContainerNode.current), paginatedComponent]
  });
}

Pagination.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Aria label that will be applied to the Paginator container. */
  "aria-label": PropTypes.string,
  children: PropTypes.func,

  /** Container where Paginator component will be rendered. */
  container: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }),
  data: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-line react/forbid-prop-types

  /** Number of items to be displayed in a page. Only positive numbers are applicable. */
  defaultItemsPerPage: PropTypes.number,
  defaultPage: PropTypes.number,

  /** Disables the Paginator component if configured to true. */
  disabled: PropTypes.bool,

  /** Number of items to be displayed in a page. Only positive numbers are applicable. */
  itemsPerPage: PropTypes.number,

  /** Does not render the itemsPerPageMenu in the accordion if configured to true. */

  /**
   * Use this prop to set the position of the tooltips for the next
   * and previous buttons of the Paginator. This will be an object value
   * for independent positioning of the tooltips. The capability to accept
   * a single string value is maintained to not break the previous API.
   * If a string value is received, a default of previous: "top-right",
   * next: "bottom-right" will be applied.
   */
  navigationTooltipPosition: PropTypes.oneOfType([PropTypes.oneOf(["top-right", "top-center", "bottom-left", "bottom-center", "bottom-right", "left", "right"]), PropTypes.shape({
    previous: PropTypes.oneOf(["top-right", "top-center", "bottom-left", "bottom-center", "bottom-right", "left", "right"]),
    next: PropTypes.oneOf(["top-right", "top-center", "bottom-left", "bottom-center", "bottom-right", "left", "right"])
  })]),
  noItemsPerPageMenu: PropTypes.bool,

  /**
   * Callback that will be executed when user navigates across pages.
   * It receives the new page number and items per page as arguments.
   * @param {Number} page - Page number that is selected.
   * @param {Number} itemsPerPage - Number of items which need to be displayed in current page.
   */
  onPagination: PropTypes.func,

  /** Currently viewed page. Only positive numbers are applicable. */
  page: PropTypes.number,

  /** Options which will be displayed in items per page menu. Maximum of 5 options can be passed.  */
  pageSizes: PropTypes.arrayOf(PropTypes.number),

  /** Override for strings used within the component. */
  paginatorCustomStrings: PropTypes.shape({
    /**
     * Connector for buttons' aria label in View Items menu.
     * Default to "Items".
     * E.g. `Items 1 through 50` in View Items Menu.
     */
    items: PropTypes.string,

    /**
     * Connector for trigger control label and navigation buttons' aria label.
     * Default to "items".
     * E.g.
     * `1 to 50 of 310 items` in trigger control label.
     * `Previous 100 items` in Previous aria label.
     * `Next 100 items` in Next aria label.
     */
    itemsInTrigger: PropTypes.string,

    /**
     * Connector for buttons' label and aria label in Items Per Page menu.
     * Default to "items per page".
     * E.g. `50 items per page` in Items Per Page Menu.
     */
    itemsPerPage: PropTypes.string,

    /**
     * Label for the Items Per Page title button.
     * Default to "Items per page".
     */
    itemsPerPageTitle: PropTypes.string,

    /**
     * Aria label for the Items Per Page title button.
     * Default to "Change numbers of items per page".
     */
    itemsPerPageAriaLabel: PropTypes.string,

    /**
     * Label for the Next button displayed in View Items Menu.
     * Default to "Next"
     */
    next: PropTypes.string,

    /**
     * Hidden instructional text for the next button in the View Items Menu.
     * Used for a11y purposes.
     *
     * Default to "Navigates to next pages of items to choose from for display."
     */
    nextButtonDetails: PropTypes.string,

    /**
     * Connector for trigger control label and aria label.
     * Default to "of".
     * E.g. `1 to 100 of 310 items`.
     */
    of: PropTypes.string,

    /**
     * Label for the Previous button displayed in View Items Menu.
     * Default to "Previous".
     */
    previous: PropTypes.string,

    /**
     * Hidden instructional text for the previous button in the View Items Menu.
     * Used for a11y purposes.
     *
     * Default to "Navigates to previous pages of items to choose from for display."
     */
    previousButtonDetails: PropTypes.string,

    /**
     * Connector for selected button's aria label in Paginator.
     * Default to "selected".
     * E.g.
     * `Items 1 through 50, selected` in View Items Menu.
     * `50 items per page, selected` in Items Per Page Menu.
     */
    selected: PropTypes.string,

    /**
     * Connector for button labels in Paginator.
     * Default to "to".
     * E.g.
     * `1 to 10` in View Items Menu.
     * `1 to 50 of 310 items` in trigger control.
     */
    to: PropTypes.string,

    /**
     * Connector between `from` and `to` of buttons' aria label in Paginator.
     * Default to "through".
     * E.g.
     * `Items 1 through 50` in View Items Menu.
     * `1 through 50 of 310 items` in trigger control.
     */
    through: PropTypes.string,

    /**
     * Connector for Paginator selection status text.
     * It will be used for Accessibility purpose.
     * Default to "Viewing".
     * E.g. `Viewing 50 to 100 of 310 items` will be announced
     * on repsective selection.
     */
    viewing: PropTypes.string,

    /**
     * Label for the View Items title button.
     * Default to "View Items".
     */
    viewItemsTitle: PropTypes.string,

    /**
     * Aria label for the View Items title button.
     * Default to "Change item selection".
     */
    viewItemsTitleAriaLabel: PropTypes.string
  }),

  /** Indicates if the pagination is remote(server side) or not. */
  remote: PropTypes.bool,

  /**
   * Provides the total rows available across the data set.
   */
  totalItems: PropTypes.number
} : {};
Pagination.defaultProps = {
  data: [],
  remote: false,
  page: null,
  container: null,
  children: null,
  onPagination: null,
  itemsPerPage: null,
  totalItems: 0,
  pageSizes: PAGINATOR_PAGE_SIZES,
  noItemsPerPageMenu: false,
  disabled: false,
  "aria-label": null,
  paginatorCustomStrings: {},
  defaultItemsPerPage: 25,
  defaultPage: 1,
  navigationTooltipPosition: "top-right"
};
export default Pagination;